import { when } from 'mobx';
import { observer } from 'mobx-react-lite';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { UniversalSelect } from '@feathr/components';
import { useStore } from '@feathr/extender/state';

import type { IDonorOption } from './TransactionFilters.types';

const SEARCH_PAGE_SIZE = 20;

interface IProps {
  /**
   * Donors visible in the table (from loaded historical + realtime events).
   * Used as the default option list before the user starts typing.
   */
  visibleDonors: IDonorOption[];
  /** Currently selected donor filters. */
  value: IDonorOption[];
  onChange: (selected: IDonorOption[]) => void;
}

/**
 * Multi-select async donor picker for the transaction table filter drawer.
 *
 * Behavior:
 * - When the input is empty, the dropdown shows the donors currently visible
 *   in the table (passed in as `visibleDonors`).
 * - When the user types, fires a `Persons.list` wildcard search across the
 *   `name`, `first_name`, and `last_name` fields, AND-ed with a donation
 *   activity predicate so the dropdown only surfaces actual donors.
 */
function DonorFilterSelect({ visibleDonors, value, onChange }: Readonly<IProps>): JSX.Element {
  const { Persons } = useStore();
  const { t } = useTranslation();

  async function loadOptions(inputValue: string): Promise<IDonorOption[]> {
    const trimmed = inputValue.trim();
    if (trimmed === '') {
      return visibleDonors;
    }

    const wildcard = `*${trimmed}*`;
    const response = Persons.list(
      {
        // (name or first_name or last_name match) AND (has donation activity)
        mode: 'match_all',
        predicates: [
          {
            kind: 'attribute',
            group_mode: 'match_any',
            group: [
              {
                kind: 'attribute',
                attr_against: 'name',
                attr_type: 'string',
                comparison: 'wildcard',
                value: wildcard,
              },
              {
                kind: 'attribute',
                attr_against: 'first_name',
                attr_type: 'string',
                comparison: 'wildcard',
                value: wildcard,
              },
              {
                kind: 'attribute',
                attr_against: 'last_name',
                attr_type: 'string',
                comparison: 'wildcard',
                value: wildcard,
              },
            ],
          },
          {
            kind: 'activity',
            attr_against: 'flvr',
            attr_type: 'string',
            comparison: 'eq',
            value: 'donation',
          },
        ],
        lookback_mode: 'unbounded',
        pagination: { page: 0, page_size: SEARCH_PAGE_SIZE },
      },
      { url: Persons.url('page') },
    );

    await when(() => !response.isPending);
    // Person.name already falls back to "Unknown Person" — no extra fallback needed.
    return response.models.map((person) => ({ id: person.id, name: person.name }));
  }

  function handleSelectMulti(selected: IDonorOption[]): void {
    onChange(selected);
  }

  return (
    <UniversalSelect<IDonorOption>
      async={true}
      defaultOptions={visibleDonors}
      isMulti={true}
      loadOptions={loadOptions}
      name={'donor-filter'}
      onSelectMulti={handleSelectMulti}
      placeholder={t('All donors')}
      value={value}
    />
  );
}

export default observer(DonorFilterSelect);
